import { PROVISIONAL, countLine, fptsText } from './fantasyModel.js'

// The fantasy layer's three pieces of markup: the panel, the crew board inside it, and the chips on a card.
// P6-333a (GN-6a). The design is `docs/design-briefs/hoops/D2D Hoops v1.dc.html` (the points league).
//
// ═══════════════════════════════════════════════════════════════════════════
// 🔴 NOTHING HERE RENDERS WITHOUT A LEAGUE, AND NOTHING HERE DECIDES THAT
// ═══════════════════════════════════════════════════════════════════════════
//
// Every caller draws these ONLY when `HoopsPanel` holds the member's own blob (GN2: a crew member outside the league
// sees no chip, no empty tab, no nudge). The components take models, not the blob, and return `null` for an empty one,
// so there is no state of them that is "a heading over nothing".
//
// ═══════════════════════════════════════════════════════════════════════════
// 🔴 THE WORD "PROVISIONAL" IS DRAWN BESIDE EVERY FIGURE (the design's flag 7)
// ═══════════════════════════════════════════════════════════════════════════
//
// The panel's kicker, its crew board's heading and the chips' label each carry it, and the panel says in a sentence that
// Yahoo's numbers replace ours tomorrow morning. `PROVISIONAL` is `fantasyModel.js`'s one constant for the word.
//
// ⚠️ EMBER MEANS YOU (the design's colour role). The viewer's own total, their crew row and their chips are ember; nobody
// else's is, and a team's colour is never used for a person.

/** The viewer's players on a card. `asOf` is set on a stale card (`as of 8:41 PM`), the design's `mineLabel`. */
export function FantasyChips({ chips, asOf = null, testId = 'hoops-mine' }) {
  if (!Array.isArray(chips) || chips.length === 0) return null
  return (
    <div className="d2d-hoops__mine" data-testid={testId}>
      <span className="d2d-hoops__minelabel d2d-micro">
        Your players {'·'} {PROVISIONAL} fpts{asOf === null ? '' : ` ${'·'} as of ${asOf}`}
      </span>
      <div className="d2d-hoops__minechips">
        {chips.map((chip) =>
          chip.kind === 'nolive' ? (
            <span
              key={`${chip.name}:${chip.team ?? ''}`}
              className="d2d-hoops__minechip d2d-hoops__minechip--nolive"
              data-testid="hoops-mine-nolive"
            >
              <span className="d2d-hoops__minename">{chip.short}</span>
              <span className="d2d-hoops__minefig">no live stats</span>
            </span>
          ) : (
            <span
              key={`${chip.name}:${chip.team ?? ''}`}
              className="d2d-hoops__minechip"
              data-testid="hoops-mine-chip"
              data-kind={chip.kind}
              data-on-floor={chip.onFloor ? 'true' : 'false'}
            >
              {chip.onFloor ? <span className="d2d-hoops__minedot" aria-label="on the floor" /> : null}
              <span className="d2d-hoops__minename">{chip.short}</span>
              <span className="d2d-hoops__minefig" data-testid="hoops-mine-fpts">
                {chip.kind === 'pre' ? 'yet to tip' : chip.kind === 'waiting' ? 'not in yet' : fptsText(chip.fpts)}
              </span>
            </span>
          ),
        )}
      </div>
    </div>
  )
}

function CrewRow({ row }) {
  return (
    <li className="d2d-hoops__crewrow" data-testid="hoops-crew-row" data-you={row.you ? 'true' : 'false'}>
      <span className="d2d-hoops__crewrank d2d-micro">{row.rank}</span>
      <span className="d2d-hoops__crewdisc d2d-micro" aria-hidden="true">
        {row.name.charAt(0).toUpperCase()}
      </span>
      <span className="d2d-hoops__crewwho">
        <span className="d2d-hoops__crewname">
          {row.name}
          {row.you ? <span className="d2d-hoops__crewyou d2d-micro"> you</span> : null}
        </span>
        <span className="d2d-hoops__crewsub d2d-micro">
          {row.played} played {'·'} {row.onFloor} on the floor
        </span>
      </span>
      <span className="d2d-hoops__crewtotal" data-testid="hoops-crew-total">
        {fptsText(row.total)}
      </span>
    </li>
  )
}

/** The panel: the viewer's night, who has no live stats, the crew board, and what the numbers are. */
export default function FantasyPanel({ model }) {
  if (model === null || model === undefined) return null
  return (
    <section
      className="d2d-hoops__fantasy"
      data-testid="hoops-fantasy"
      aria-label={`Your fantasy night, ${PROVISIONAL}`}
    >
      <div className="d2d-hoops__fantasyhead">
        <span className="d2d-hoops__fantasykicker d2d-micro" data-testid="hoops-fantasy-kicker">
          Your night {'·'} {PROVISIONAL}
        </span>
        <span className="d2d-hoops__fantasysrc d2d-micro">Yahoo + live box scores</span>
      </div>

      <div className="d2d-hoops__fantasytotal">
        <div className="d2d-hoops__fantasytotalline">
          <span className="d2d-hoops__fantasytotalfig" data-testid="hoops-fantasy-total">
            {fptsText(model.total)}
          </span>
          <span className="d2d-hoops__fantasytotallabel">fantasy points tonight</span>
        </div>
        <div className="d2d-hoops__fantasycount" data-testid="hoops-fantasy-count">
          {countLine(model)}
        </div>
      </div>

      {model.unscored.length > 0 ? (
        <p className="d2d-hoops__fantasynote" data-testid="hoops-fantasy-unscored">
          This total leaves out {model.unscored.join(', ')}, which a box score cannot give.
        </p>
      ) : null}

      {model.noLive.length > 0 ? (
        <div className="d2d-hoops__nolive" data-testid="hoops-fantasy-nolive">
          {model.noLive.map((player) => (
            <span className="d2d-hoops__nolivelist" key={`${player.name}:${player.team ?? ''}`}>
              <span className="d2d-hoops__nolivename">{player.name}</span>
              {player.team === null ? '' : ` ${'·'} ${player.team}`} {'·'} no live stats
            </span>
          ))}
        </div>
      ) : null}

      {model.crew.length > 0 ? (
        <div className="d2d-hoops__crew" data-testid="hoops-crew">
          <span className="d2d-hoops__crewhead d2d-micro" data-testid="hoops-crew-heading">
            Crew board {'·'} tonight {'·'} {PROVISIONAL} fpts
          </span>
          <ol className="d2d-hoops__crewlist">
            {model.crew.map((row) => (
              <CrewRow key={row.name} row={row} />
            ))}
          </ol>
        </div>
      ) : null}

      <div className="d2d-hoops__fantasyfoot">
        <span className="d2d-hoops__fantasynote" data-testid="hoops-fantasy-note">
          Every number here is ours, built from live box scores. Yahoo{'’'}s replace them tomorrow morning.
        </span>
        <span className="d2d-hoops__fantasyleague d2d-micro" data-testid="hoops-fantasy-league">
          {model.league}
        </span>
      </div>
    </section>
  )
}
